import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { AudioMiniPlayerService, MiniPlayerState } from './audio-mini-player.service';
import { AudioPreviewComponent } from '../audio-preview/audio-preview.component';
import { VideoPreviewComponent } from '../video-preview/video-preview.component';

/**
 * Floating mini-player pinned to the bottom-right. Rendered once at the app root so it
 * survives route navigation. Audio shows a compact bar; video shows a small window with
 * the live video frame (the service's <video> element is moved into this component's DOM,
 * so playback never stops during the hand-off).
 */
@Component({
  standalone: true,
  imports: [CommonModule, MatIconModule],
  selector: 'app-audio-mini-player',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container *ngIf="state as s">
      <!-- VIDEO: small window with the live frame -->
      <div class="amp amp--video" *ngIf="s.isVideo">
        <div class="amp__vtop">
          <span class="amp__title" [title]="s.title">{{ s.title }}</span>
          <button class="amp__icon" (click)="expand()" title="Open full player"><mat-icon>open_in_full</mat-icon></button>
          <button class="amp__icon" (click)="svc.stop()" title="Close"><mat-icon>close</mat-icon></button>
        </div>
        <div #videoHost class="amp__vhost" (click)="svc.togglePlay()"></div>
        <div class="amp__vbar">
          <button class="amp__vplay" (click)="svc.togglePlay()" [title]="s.isPlaying ? 'Pause' : 'Play'">
            <mat-icon>{{ s.isPlaying ? 'pause' : 'play_arrow' }}</mat-icon>
          </button>
          <div class="amp__bar" (click)="seek($event)"><div class="amp__bar-fill" [style.width.%]="progress(s)"></div></div>
          <span class="amp__time">{{ fmt(s.currentTime) }} / {{ fmt(s.duration) }}</span>
        </div>
      </div>

      <!-- AUDIO: compact bar -->
      <div class="amp amp--audio" *ngIf="!s.isVideo">
        <button class="amp__play" (click)="svc.togglePlay()" [title]="s.isPlaying ? 'Pause' : 'Play'">
          <mat-icon>{{ s.isPlaying ? 'pause' : 'play_arrow' }}</mat-icon>
        </button>
        <div class="amp__body">
          <div class="amp__title" [title]="s.title">{{ s.title }}</div>
          <div class="amp__bar" (click)="seek($event)"><div class="amp__bar-fill" [style.width.%]="progress(s)"></div></div>
          <div class="amp__time">{{ fmt(s.currentTime) }} / {{ fmt(s.duration) }}</div>
        </div>
        <button class="amp__icon" (click)="expand()" title="Open full player"><mat-icon>open_in_full</mat-icon></button>
        <button class="amp__icon" (click)="svc.stop()" title="Close"><mat-icon>close</mat-icon></button>
      </div>
    </ng-container>
  `,
  styles: [`
    .amp {
      position: fixed; right: 20px; bottom: 20px; z-index: 998;
      background: linear-gradient(to right, #0f172a, #1e293b);
      color: #e2e8f0; border: 1px solid rgba(255,255,255,0.08);
      border-radius: 12px; box-shadow: 0 12px 32px rgba(0,0,0,0.35);
    }
    .amp--audio { display: flex; align-items: center; gap: 10px; width: 340px; max-width: calc(100vw - 40px); padding: 10px 12px; }
    .amp--video { width: 340px; max-width: calc(100vw - 40px); padding: 8px; }
    .amp__vtop { display: flex; align-items: center; gap: 6px; padding: 2px 4px 8px; }
    .amp__vtop .amp__title { flex: 1 1 auto; }
    .amp__vhost {
      width: 100%; aspect-ratio: 16 / 9; background: #000; border-radius: 8px; overflow: hidden; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      ::ng-deep video { width: 100%; height: 100%; object-fit: contain; background: #000; display: block; }
    }
    .amp__vbar { display: flex; align-items: center; gap: 8px; padding: 8px 4px 2px; }
    .amp__vplay {
      flex: 0 0 auto; width: 30px; height: 30px; border-radius: 50%; border: none; cursor: pointer;
      background: #ff6b35; color: #fff; display: inline-flex; align-items: center; justify-content: center;
      mat-icon { font-size: 18px; width: 18px; height: 18px; }
      &:hover { background: #f0602c; }
    }
    .amp__play {
      flex: 0 0 auto; width: 38px; height: 38px; border-radius: 50%; border: none; cursor: pointer;
      background: #ff6b35; color: #fff; display: inline-flex; align-items: center; justify-content: center;
      mat-icon { font-size: 22px; width: 22px; height: 22px; }
      &:hover { background: #f0602c; }
    }
    .amp__body { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
    .amp__title { font-size: 12px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .amp__bar { flex: 1 1 auto; height: 5px; border-radius: 3px; background: rgba(255,255,255,0.18); cursor: pointer; position: relative; }
    .amp__bar-fill { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 3px; background: #ff6b35; }
    .amp__time { font-size: 10px; color: #94a3b8; white-space: nowrap; }
    .amp__icon {
      flex: 0 0 auto; width: 30px; height: 30px; border: none; background: transparent; color: #cbd5e1; cursor: pointer;
      border-radius: 8px; display: inline-flex; align-items: center; justify-content: center;
      mat-icon { font-size: 18px; width: 18px; height: 18px; }
      &:hover { background: rgba(255,255,255,0.1); color: #fff; }
    }
  `],
})
export class AudioMiniPlayerComponent implements OnInit, OnDestroy, AfterViewChecked {
  state: MiniPlayerState | null = null;
  private sub?: Subscription;
  @ViewChild('videoHost') videoHost?: ElementRef<HTMLElement>;

  constructor(public svc: AudioMiniPlayerService, private dialog: MatDialog, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.sub = this.svc.state$.subscribe(s => { this.state = s; this.cdr.markForCheck(); });
  }
  ngAfterViewChecked(): void { this.attachVideo(); }
  ngOnDestroy(): void { this.sub?.unsubscribe(); }

  /** Move the service's live <video> element into the window (once it's rendered). */
  private attachVideo(): void {
    if (!this.state?.isVideo) return;
    const host = this.videoHost?.nativeElement;
    const el = this.svc.element as HTMLVideoElement | null;
    if (host && el && el.parentElement !== host) host.appendChild(el);
  }

  progress(s: MiniPlayerState): number { return s.duration > 0 ? (s.currentTime / s.duration) * 100 : 0; }

  seek(e: MouseEvent): void {
    const bar = e.currentTarget as HTMLElement;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const dur = this.state?.duration || 0;
    if (dur) this.svc.seek(pct * dur);
  }

  /** Re-open the full preview at the current position. */
  expand(): void {
    const { model, currentTime, isPlaying, isVideo } = this.svc.hideForReopen();
    if (!model) return;
    const data = { ...model, startTime: currentTime, autoplay: isPlaying };
    if (isVideo) {
      this.dialog.open(VideoPreviewComponent, { width: '90vw', maxWidth: '1100px', data, panelClass: 'media-preview-dialog' });
    } else {
      this.dialog.open(AudioPreviewComponent, { width: '680px', maxWidth: '90vw', data, panelClass: 'media-preview-dialog' });
    }
  }

  fmt(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
}
